import { type CSSObject } from "@emotion/react";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useHistory } from "react-router";
import { Breakpoints, Colors, Icon, IconBlock } from "swing-components";

import { AlertError, ButtonScoreWrapper, ContentSingleColumn } from "~components";
import { createEducationEditUrl, createEducationNewUrl } from "~pages";
import { GET, ProfileData, translate } from "~utils";

import { ScorePage } from "../../ScoreTemplates";

export function EducationLanding() {
  const { data, error, isLoading } = useQuery<{ data: ProfileData }, AxiosError>({
    queryKey: ["fetchProfile"],
    queryFn: () => GET("/api/sub/profile"),
  });

  return (
    <EducationLandingView education={data?.data.education} error={error} isLoading={isLoading} />
  );
}

export type Education = {
  id: number;
  name: string;
};

type EducationLandingViewProps = {
  education?: Education[];
  error?: AxiosError | null;
  isLoading?: boolean;
};

function EducationLandingView(props: EducationLandingViewProps) {
  const { education = [], error, isLoading } = props;

  /* Hooks */
  const history = useHistory();

  return (
    <ScorePage title={translate("PAGE_TITLE_EDUCATION_LANDING")} isLoading={isLoading} hasBack>
      {error ? (
        <AlertError margin="0px" />
      ) : (
        <ContentSingleColumn>
          <div css={styles.educationLandingContainer}>
            <span>{translate("EDUCATION_LANDING_SUB_HEADER")}</span>
            {education.length > 0 && (
              <div css={styles.educationContent}>
                {education.map((edu, idx) => {
                  const { id, name } = edu;
                  const isLast = idx === education.length - 1;
                  return (
                    <div key={id}>
                      <IconBlock
                        content={<span>{name}</span>}
                        rightIcon={{
                          icon: <Icon name="Edit" color={Colors.blue500} />,
                          onClick: () => history.push(createEducationEditUrl(`${id}`)),
                        }}
                        hasDivider={!isLast}
                      />
                    </div>
                  );
                })}
              </div>
            )}
            {(!education || education.length < 3) && (
              <ButtonScoreWrapper
                buttonPrimary={{
                  label: `+ ${translate("PAGE_TITLE_EDUCATION_EDIT")}`,
                  expand: "block",
                  onClick: () => history.push(createEducationNewUrl()),
                }}
              />
            )}
          </div>
        </ContentSingleColumn>
      )}
    </ScorePage>
  );
}

/********** Styles **********/
const styles = {
  educationLandingContainer: {
    padding: "16px 16px 0",

    [Breakpoints.mediumDeviceAndUp]: {
      paddingTop: 0,
    },
  },
  educationContent: {
    marginTop: 16,
    borderRadius: 8,
    display: "flex",
    flexDirection: "column",
    gap: 10,
    backgroundColor: Colors.white200,

    [Breakpoints.mediumDeviceAndUp]: {
      marginTop: 24,
      padding: 16,
    },
  },
} satisfies Record<string, CSSObject>;
